import React, {FC, useContext, useEffect, useState} from "react";
import {AuthContext} from "contexts";
import {AbuseWord, abuseWordsService, hasAppIdsSupport} from "services/abuseWordsService";
import {AgGridReact} from "ag-grid-react";
import {useAbuseWordsTable} from "pages/AbuseWords/useAbuseWordsTable";
import {useWindowDimensions} from "hooks";
import {AbuseWordModal} from "pages/AbuseWords/components/AbuseWordModal";
import {AbuseRuleCheckModal} from "pages/AbuseWords/components/AbuseRuleCheckModal";
import {AbuseStats, statsService} from "services";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";

const MySwal = withReactContent(Swal);

export const AbuseWordsPage: FC = () => {
    const {env} = useContext(AuthContext);

    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingWord, setEditingWord] = useState<AbuseWord | null>(null);
    const [words, setWords] = useState<AbuseWord[]>([]);
    const [stats, setStats] = useState<AbuseStats[]>([]);
    const [appIdsSupported, setAppIdsSupported] = useState(false);

    const [checkRuleId, setCheckRuleId] = useState<number | null>(null);
    const [deletingId, setDeletingId] = useState<number | null>(null);


    const {columnDefs} = useAbuseWordsTable(setEditingId, setDeletingId, setCheckRuleId, appIdsSupported);
    const {height} = useWindowDimensions();

    const mergeStatsIntoWords = (next: AbuseWord[]) => {
        if (!stats.length) {
            return next;
        }
        return next.map(w => ({
            ...w,
            stats: stats.find(s => s.category === w.ruleName)
        }));
    };

    const onReset = async (didChange?: boolean) => {
        setEditingId(null);
        if (didChange && env) {
            await abuseWordsService.getAll(env).then(w => {
                if (!w) {
                    return;
                }
                setAppIdsSupported(hasAppIdsSupport(w));
                setWords(mergeStatsIntoWords(w));
            });
        }
    };

    useEffect(() => {
        if (!env) {
            return;
        }
        abuseWordsService.getAll(env).then(async w => {
            if (!w) {
                return;
            }
            setAppIdsSupported(hasAppIdsSupport(w));
            setWords(w);
            const nextStats = await statsService.getAbuse(env);
            setStats(nextStats);
            setWords(prevWords => prevWords.map(word => ({
                ...word,
                stats: nextStats.find(s => s.category === word.ruleName)
            })));
        });
    }, [env]);

    useEffect(() => {
        const deleteWord = async () => {
            const word = words.find(w => w.id === deletingId);
            if (!deletingId || !word) {
                setDeletingId(null);
                return;
            }
            const result = await MySwal.fire({
                title: 'Удалить Abuse Паттерн?',
                text: `${word.ruleName}, Паттерн: ${word?.pattern}`,
                confirmButtonText: 'Да',
                confirmButtonColor: '#D9292E',
                cancelButtonText: 'Закрыть',
                showCancelButton: true,
                showConfirmButton: true
            });
            if (result.isConfirmed && env) {
                await abuseWordsService.delete(deletingId, env);
                abuseWordsService.getAll(env).then(w => {
                    if (w) {
                        setWords(mergeStatsIntoWords(w));
                    }
                });
            }
            setDeletingId(null);
        };
        deletingId && deleteWord();
    }, [words, deletingId]);

    useEffect(() => {
        if (editingId) {
            const word = words.find(w => w.id === editingId);
            word && setEditingWord(word);
        } else {
            setEditingWord(null);
        }
    }, [words, editingId]);

    const checkWord = checkRuleId != null ? words.find(w => w.id === checkRuleId) ?? null : null;

    return <div className="pt-2 px-1 d-flex flex-column align-items-end">
        <AbuseWordModal editingWord={editingWord} onReset={onReset} appIdsSupported={appIdsSupported}/>
        <AbuseRuleCheckModal
            ruleId={checkRuleId}
            word={checkWord}
            env={env}
            onHide={() => {
                setCheckRuleId(null)
                onReset(true)
            }}
            onReloadWords={onReset}
        />
        <div className="mt-2 w-100" style={{height: height - 150, width: '100%'}}>
            <AgGridReact
                pagination={true}
                paginationPageSize={50}
                rowData={words}
                columnDefs={columnDefs}
            />
        </div>
    </div>;
};
